import React, {
  useContext, useEffect, useState,
} from 'react';
import { useIntl } from 'react-intl';
import { ReactSVG } from 'react-svg';
import { useHistory } from 'react-router';
import getConfig from '../../config';
import { appStore } from '../../state/app';
import { buildGatewayUrl, initIPFS } from '../../state/ipfs';
import CustomBtn from '../../generic/CustomBtn';
import logo from '../../assets/image/logo.svg';
import GenInformation from './Steps/GenInformation';
import ProjectLocation from './Steps/ProjectLocation';
import StepWizard from './Steps';
import RefoData from './Steps/RefoData';
import Preview from './Steps/PreviewProject';
import Loader from '../Loader/Loader';
import {
  contractMethods,
  getContract,
  getTxOutcome,
  isTooManyAccessKeysError,
  isWalletCancelError,
  TOO_MANY_ACCESS_KEYS_MESSAGE,
} from '../../utils/near-utils';
import { GAS } from '../../state/near';
import {
  convertDurationWithMetrics, haToSqm, milliToNano,
} from '../../utils/helpers';
import storage from '../../utils/storage';

const stepForm = [
  { id: 'wizardForm.step1.generalInformation' },
  { id: 'wizardForm.step2.projectLocation' },
  { id: 'wizardForm.step3.additionalInformation' },
  { id: 'wizardForm.step4.preview' },
];

const { deposit, projectDurationType } = getConfig();

const WizardForm = ({
  setRequestError,
  setRequestErrorMessage = () => {},
  setShowModal,
  onProjectCreated = async () => {},
}) => {
  const translate = useIntl();
  const history = useHistory();
  const { state, update } = useContext(appStore);
  const { app, account } = state;
  const { profile } = app;
  const [step, setStep] = useState(0);
  const [defaultState, setDefaultState] = useState({
    developer: translate.formatMessage({ id: 'wizardForm.setupAccount' }),
  });
  const [loadProfile, setProfile] = useState(false);

  useEffect(() => {
    if (profile && Object.keys(profile).length > 0) {
      setDefaultState({
        ...defaultState,
        developer: profile?.name || '',
        website: profile?.website || '',
      });
    } else {
      setDefaultState({
        ...defaultState,
        developer: translate.formatMessage({ id: 'wizardForm.setupAccount' }),
        website: '',
      });
    }
    setProfile(true); translate.formatMessage({ id: 'wizardForm.setupAccount' });
  }, [profile]);

  const nextPage = () => {
    setStep(step + 1);
    window.scrollTo(0, 0);
  };

  const prevPage = () => {
    setStep(step - 1);
    window.scrollTo(0, 0);
  };

  const prepInterestedInCarbon = (projectId) => {
    const isInterestData = storage.get('isInterest') || {};
    const { isInterest = false } = isInterestData;
    storage.set('isInterest', {
      project_id: projectId,
      isInterest,
    });
  };

  const handleMint = async (data) => {
    try {
      setRequestErrorMessage('');
      update('loading', true);
      const signerId = account.accountId || '';
      const ipfs = await initIPFS();
      const { path } = await ipfs.add(JSON.stringify({ ...data, signer_id: signerId }));

      if (typeof path !== 'string') {
        throw new Error('IPFS path is not available');
      }
      const { type, square, projectDuration, name, iconCidDir } = data;
      const projectId = `${Date.now()}`;
      prepInterestedInCarbon(projectId);
      const sqmArea = haToSqm(square);
      const duration = convertDurationWithMetrics(projectDuration, projectDurationType);
      const durationNano = milliToNano(duration);
      const contract = getContract(account, contractMethods, 0);

      const txResult = await contract.add_project({
        project_id: projectId,
        project_type: type,
        area: sqmArea.toString(),
        duration: durationNano.toString(),
        title: name,
        media: buildGatewayUrl(iconCidDir),
        reference: `/ipfs/${path}`,
      }, GAS, deposit);
      const { isSuccess, methodName } = getTxOutcome(txResult);
      if (!isSuccess || methodName !== 'add_project') {
        throw new Error('add_project tx was not successful');
      }
      await onProjectCreated(projectId);
    } catch (e) {
      if (isWalletCancelError(e)) {
        return;
      }
      if (isTooManyAccessKeysError(e)) {
        setRequestErrorMessage(TOO_MANY_ACCESS_KEYS_MESSAGE);
      }
      setRequestError(true);
      setShowModal(true);
    } finally {
      update('loading', false);
    }
  };

  return (
    <section className="wizard">
      <div className="wizard__wrapper">
        <div className="wizard__intro">
          <div className="wizard__logo">
            <ReactSVG src={logo} />
          </div>
          {step < 4
            ? <h2 className="wizard__title">{translate.formatMessage({ id: 'wizardForm.title' })}</h2>
            : <h2 className="wizard__title center">{translate.formatMessage({ id: 'wizardForm.title.preview' })}</h2>}
          <div className="wizard__wrapper-btn">
            <CustomBtn
              label={translate.formatMessage({ id: 'button-cancel' })}
              customClass="btn__close"
              iconClass="icon-close"
              handleClick={() => {
                history.push('/');
              }}
            />
          </div>
        </div>
        {step < 4 && <StepWizard step={step} stepForm={stepForm} />}
        {loadProfile && (
          <div className="wizard__gen-wrapper">
            {state.loading && (
            <div className="cropper__loader">
              <Loader />
            </div>
            )}
            <GenInformation
              step={step}
              state={defaultState}
              setState={setDefaultState}
              nextPage={nextPage}
              setShowModal={setShowModal}
              setRequestError={setRequestError}
            />
            <ProjectLocation
              step={step}
              state={defaultState}
              setState={setDefaultState}
              nextPage={nextPage}
              prevPage={prevPage}
              setShowModal={setShowModal}
              setRequestError={setRequestError}
            />
            <RefoData
              step={step}
              state={defaultState}
              setState={setDefaultState}
              nextPage={nextPage}
              prevPage={prevPage}
              handleMint={handleMint}
            />
            <Preview
              state={defaultState}
              setState={setDefaultState}
              handleMint={handleMint}
              prevPage={prevPage}
              step={step}
              setStep={setStep}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default WizardForm;
